"use strict";

// Price preservation and price verification are separate decisions. A chat
// holding a number must never turn an AI estimate into a verified tech price.
// Mirrored in src/lib/priceProvenance.js; regression tests compare both copies.
const sourceObject = (ps) => ps && typeof ps === "object" ? ps : {};
const tierOf = (ps) => typeof ps === "string" ? ps : ps?.tier;

function isUnverifiedPriceSource(ps) {
  const tier = tierOf(ps);
  return !tier || tier === "web_placeholder" || tier === "tech_set";
}

function aiEstimateSource(cost, origin = "ai") {
  return {
    tier: "web_placeholder", origin, confidence: "low",
    ai_estimate: Number.isFinite(Number(cost)) ? Number(cost) : null,
    flag: origin === "unknown"
      ? "price source unverified — confirm the supplier and amount"
      : "AI estimate — no verified supplier price; confirm before sending",
  };
}

function bookReference(src, cost) {
  if (["supplier_prices", "supplier_quote"].includes(src.tier) && src.supplier_name && cost != null) {
    return {
      supplier_name: src.supplier_name, supplier_sku: src.supplier_sku || null,
      supplier_description: src.supplier_description || null,
      unit_price: Number(cost), date_recorded: src.date_recorded || null,
    };
  }
  return src.book_match || null;
}

function manualPriceSource(previous, previousCost) {
  const src = sourceObject(previous);
  const ref = bookReference(src, previousCost);
  return {
    tier: "tech_set", origin: "manual", confidence: "low",
    flag: "manual price — no supplier verification for this amount",
    ...(ref ? { book_match: ref } : {}),
  };
}

function heldPriceSource(m) {
  const src = sourceObject(m._price_source);
  // A rename holds the old source as well as its number. If the part identity
  // was stripped, the old supplier price no longer verifies this new wording.
  if (m._pinKind === "rename") {
    if (!src.tier || (src.tier === "supplier_prices" && !src.supplier_name)) return aiEstimateSource(m.our_cost, "unknown");
    return src;
  }
  // Old editor pins already have a source. Do not relabel them or raise their
  // confidence just because another field was edited in the same draft.
  if (!m._pinKind && src.tier) return src;

  const bookCost = m._bookCost != null ? m._bookCost : m._bookWas;
  if (["supplier_prices", "supplier_quote"].includes(src.tier) && src.supplier_name
      && bookCost != null && Math.abs(Number(bookCost) - Number(m.our_cost)) < 0.000001) return src;
  const ref = bookReference(src, bookCost);
  const out = m._pinOrigin === "manual"
    ? manualPriceSource(src, bookCost)
    : aiEstimateSource(m.our_cost);
  return { ...out, ...(ref ? { book_match: ref } : {}) };
}

module.exports = { isUnverifiedPriceSource, aiEstimateSource, manualPriceSource, heldPriceSource };
